import { useState, useEffect, useContext, type ChangeEvent } from "react";
import { useLocalization } from "@fluent/react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ContentCard,
  TextInput,
  Button,
  Row,
  MenuButton,
  Avatar,
  Chip,
  Tooltip,
  Select,
} from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import { useRolesContext, useRoleDetail } from "byzantine/src/Role/RolesProvider";
import { type Role } from "byzantine/src/Role/Role";
import OrganizationUser from "byzantine/src/OrganizationUser";
import { useOrganizationUserContext } from "byzantine/src/OrganizationUser/OrganizationUserProvider";
import InstitutionSettingsContext from "../contexts/InstitutionSettingsContext";
import {
  splitRoles,
  filterRolesByName,
  DEFAULT_ROLE_NAMES,
  getExternalAccountSubtitle,
  LIMITS_DATA,
  hasAnyWritePermissionForLimits,
  getActiveUserCountForRole,
  getActiveUsersForRole,
} from "./OrganizationRoles.helpers";
import LongRoleTile from "./LongRoleTile";
import RoleDetailLoadingStatus from "./RoleDetailLoadingStatus";
import RoleAccountPermissionsView from "./RoleAccountPermissionsView";
import RoleLevelPermissionsView from "./RoleLevelPermissionsView";
import RoleLimitsView from "./RoleLimitsView";
import DeleteRoleModal from "./DeleteRoleModal";
import styles from "./OrganizationRoles.module.scss";

type OrganizationRolesProps = {
  organizationUuid?: UUID;
  allAccounts?: (API.Account | API.ExternalAccount)[];
};

type RoleTileProps = {
  role: Role;
  selectedUuid?: string | null;
  organizationUsers?: OrganizationUser[];
  isDefault?: boolean;
  l10n: ReturnType<typeof useLocalization>["l10n"];
  onSelect: (roleUuid: string) => void;
};

function RoleTile({
  role,
  selectedUuid,
  organizationUsers,
  isDefault,
  l10n,
  onSelect,
}: RoleTileProps) {
  const handleClick = () => {
    if (role.uuid) {
      onSelect(role.uuid);
    }
  };

  const isSelected = role.uuid === selectedUuid;
  const userCount = getActiveUserCountForRole(role.uuid, organizationUsers);
  const userCountText = l10n.getString("org-role-user-count", {
    count: userCount,
  });

  return (
    <div
      onClick={handleClick}
      onKeyDown={(e) =>
        e.key === "Enter" || e.key === " " ? handleClick() : undefined
      }
      role="button"
      tabIndex={0}
      className={`${styles.roleTile} ${isSelected ? styles.selected : ""}`}
    >
      <div className={styles.roleName}>
        <Row gapSize="xs" alignItems="center">
          <div>{role.name}</div>
          {isDefault && (
            <Chip
              isLabelVisible={false}
              startIcon="lock"
              label={l10n.getString("org-role-locked")}
            />
          )}
        </Row>
      </div>
      <p className={styles.roleUserCount}>{userCountText}</p>
    </div>
  );
}

type RoleInfoProps = {
  role: Role | null;
  organizationUsers?: OrganizationUser[];
  allAccounts?: (API.Account | API.ExternalAccount)[];
  orgLimits?: API.Limits.ByType;
  hasInstitutionLoaded?: boolean;
  l10n: ReturnType<typeof useLocalization>["l10n"];
  assignableRoles: Role[];
  onNavigateToEditRole: (roleUuid: string) => void;
  onNavigateToDuplicateRole: (roleUuid: string) => void;
};

function RoleInfo({
  role,
  organizationUsers,
  allAccounts,
  orgLimits,
  hasInstitutionLoaded,
  l10n,
  assignableRoles,
  onNavigateToEditRole,
  onNavigateToDuplicateRole,
}: RoleInfoProps) {
  const { sendNotification } = useNotificationContext();
  const institution = useContext(InstitutionSettingsContext);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // Per-account permissions only come with the role detail.
  const {
    data: roleDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useRoleDetail(role?.uuid);

  // Reset expanded state when role changes
  useEffect(() => {
    setIsDeleteModalOpen(false);
  }, [role?.uuid]);

  if (!role) {
    return (
      <ContentCard kind="bordered">
        <div className={styles.emptyStateMessage}>
          {l10n.getString("org-role-empty-state")}
        </div>
      </ContentCard>
    );
  }

  const usersForRole = getActiveUsersForRole(role.uuid, organizationUsers);
  const isDefault = DEFAULT_ROLE_NAMES.has(role.name);
  const isAdmin = role.name === OrganizationUser.ROLE.ADMIN;
  const isViewer = role.name === OrganizationUser.ROLE.VIEWER;

  // Compute dynamic subtitle for "Make external transfer" permission
  const externalTransferSubtitle = getExternalAccountSubtitle(
    role,
    allAccounts
  );

  const limits: API.Limits.ByType = Object.keys(role.limits).length > 0 ? (role.limits as API.Limits.ByType) : orgLimits ?? {};

  // Check if role has write permission for at least one limit type
  const hasPermissionForAnyLimits = hasAnyWritePermissionForLimits(
    role.permissions,
    LIMITS_DATA
  );

  return (
    <ContentCard kind="bordered" paddingSize="xl">
      <div className={styles.roleInfo}>
        <Row gapSize="s">
          <Row.Item>
            <Row gapSize="xs" alignItems="center">
              <h2>{role.name}</h2>
              {isDefault && (
                <Chip
                  isLabelVisible={false}
                  startIcon="lock"
                  label={l10n.getString("org-role-locked")}
                />
              )}
            </Row>
            {role.description && (
              <p className={styles.roleDescription}>{role.description}</p>
            )}
          </Row.Item>
          {!isDefault && (
            <>
              <Row.Item shrink>
                <Button kind="secondary" size="xs" startIcon="copy" onClick={() => role.uuid && onNavigateToDuplicateRole(role.uuid)}>
                  {l10n.getString("org-role-button-duplicate")}
                </Button>
              </Row.Item>
              <Row.Item shrink>
                <Button kind="primary" size="xs" startIcon="edit" onClick={() => role.uuid && onNavigateToEditRole(role.uuid)}>
                  {l10n.getString("org-role-button-edit")}
                </Button>
              </Row.Item>
            </>
          )}
          <Row.Item shrink>
            <MenuButton>
              {!isDefault && (
              <MenuButton.Item
                label={l10n.getString("org-role-delete-role")}
                onSelect={() => setIsDeleteModalOpen(true)}
              />)}
              <MenuButton.Item
                label={l10n.getString("org-role-copy-role")}
                onSelect={async () => {
                  const url = `${window.location.origin}/permissions/roles/${role.uuid}`;
                  try {
                    await navigator.clipboard.writeText(url);
                    sendNotification({ type: "success", text: l10n.getString("org-role-copy-link-success") });
                  } catch (error) {
                    sendNotification({ type: "negative", text: l10n.getString("org-role-copy-link-error") });
                  }
                }}
              />
            </MenuButton>
          </Row.Item>
        </Row>
      </div>

      {usersForRole.length > 0 && (
        <>
          <hr className={styles.divider} />
          <div className={styles.usersSection}>
            <p
              className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
            >
              {l10n.getString("org-role-assigned-to")}
            </p>
            {usersForRole.map((user) => (
              <div key={user.uuid} className="padding--bottom--m">
                <Row gapSize="s" alignItems="center">
                  <Tooltip text={user.user.email}>
                    <Avatar
                      label={user.fullName()}
                      kind="tonal"
                      initials={user.userInitials()}
                    ></Avatar>
                  </Tooltip>
                  {user.fullName()}
                </Row>
              </div>
            ))}
          </div>
        </>
      )}
      <RoleDetailLoadingStatus
        isLoading={isDetailLoading}
        isError={isDetailError && !roleDetail}
      />
      {roleDetail && (
        <>
          <hr className={styles.divider} />
          <div className={styles.usersSection}>
            <RoleAccountPermissionsView
              key={roleDetail.uuid}
              role={roleDetail}
              accounts={(allAccounts ?? []).filter(
                (acc): acc is API.Account => !("externalAccountTypeName" in acc)
              )}
              institutionName={institution?.long_name}
              externalTransferSubtitle={externalTransferSubtitle}
              features={institution?.features}
              summaryTile={
                (isAdmin && (
                  <LongRoleTile
                    iconClass="narmi-icon-check"
                    title={l10n.getString("org-role-admin-title")}
                    description={l10n.getString("org-role-admin-description")}
                  />
                )) ||
                (isViewer && (
                  <LongRoleTile
                    iconClass="narmi-icon-eye"
                    title={l10n.getString("org-role-viewer-all-accounts-title")}
                    description={l10n.getString("org-role-viewer-description")}
                  />
                )) ||
                undefined
              }
            />
          </div>
          {!isViewer && (
            <>
              <hr className={styles.divider} />
              <RoleLevelPermissionsView
                role={roleDetail}
                features={institution?.features}
              />
            </>
          )}
        </>
      )}
      
      {hasInstitutionLoaded && Object.keys(limits).length > 0 && hasPermissionForAnyLimits && (
        <>
        <hr className={styles.divider} />
        <div className={styles.usersSection}>
          <div
            className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
          >
            {isAdmin ? l10n.getString("org-role-organization-limits") : l10n.getString("org-role-limits")}
          </div>
          {isAdmin && (<p className={"fontSize--s"} style={{ color: "var(--nds-color-gray-medium)" }}>{l10n.getString("org-role-organization-limits-subtitle")}</p>)}
          <RoleLimitsView limits={limits} permissions={role.permissions} trailingDivider />
         </div>
         </>
       )}
        <DeleteRoleModal
          role={role}
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          organizationUsers={organizationUsers}
          assignableRoles={assignableRoles}
        />
     </ContentCard>
   );
 }

function OrganizationRolesContent({
  allAccounts,
}: {
  allAccounts?: (API.Account | API.ExternalAccount)[];
}) {
  const { l10n } = useLocalization();
  const { isLoading, error, assignableRoles } = useRolesContext();
  const { sendNotification } = useNotificationContext();
  const { organizationUsers } = useOrganizationUserContext();
  const navigate = useNavigate();
  const { roleUuid } = useParams<{ roleUuid?: string }>();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const institution = useContext(InstitutionSettingsContext);
  const orgLimits = institution?.features?.limits || {};
  const hasInstitutionLoaded = !!institution?.features?.limits;

  const selectedRole = assignableRoles.find((role) => role.uuid === roleUuid) || null;

  const navigateToRole = (selectedRoleUuid: string) => {
    navigate(`/roles/${selectedRoleUuid}`);
  };

  useEffect(() => {
    if (error) {
      sendNotification({
        type: "negative",
        text: `${l10n.getString("org-role-error-toast")}: ${error.message}`,
      });
    }
  }, [error, sendNotification]);

  // Auto-select first default role on initial load if none selected
  useEffect(() => {
    if (!roleUuid && assignableRoles.length > 0) {
      const { defaultRoles } = splitRoles(assignableRoles);

      if (defaultRoles.length > 0) {
        const firstDefaultRole = defaultRoles[0];
        if (firstDefaultRole.uuid) {
          navigate(`/roles/${firstDefaultRole.uuid}`, { replace: true });
        }
      }
    }
  }, [assignableRoles, navigate, roleUuid]);

  // Clear selection if the selected role is deleted
  useEffect(() => {
    if (roleUuid && !selectedRole && assignableRoles.length > 0) {
      navigate("/roles", { replace: true });
    }
  }, [assignableRoles.length, navigate, roleUuid, selectedRole]);

  if (isLoading) {
    return (
      <div className={styles.statusContainer}>
        {l10n.getString("org-role-loading")}
      </div>
    );
  }

  const { defaultRoles, customRoles } = splitRoles(assignableRoles);

  const filteredDefaultRoles = filterRolesByName(defaultRoles, searchQuery);
  const filteredCustomRoles = filterRolesByName(customRoles, searchQuery);

  return (
    <div className={styles.container}>
      {/* Mobile role selector dropdown */}
      <div className={styles.mobileRoleSelector}>
        <Select
          value={roleUuid || ""}
          onChange={(value: string) => {
            if (value) {
              navigateToRole(value);
            }
          }}
        >
           {defaultRoles.length > 0 && (
             <Select.Category
               isFlat
               label={<span className="fontSize--xs">{l10n.getString("org-role-section-default")}</span> as unknown as string}
             >
               {defaultRoles.map((role) => (
                 <Select.Item key={role.uuid} value={role.uuid || ""}>
                   {role.name}
                 </Select.Item>
               ))}
             </Select.Category>
           )}
           {customRoles.length > 0 && (
             <Select.Category
               isFlat
               label={<span className="fontSize--xs">{l10n.getString("org-role-section-custom")}</span> as unknown as string}
             >
               {customRoles.map((role) => (
                 <Select.Item key={role.uuid} value={role.uuid || ""}>
                   {role.name}
                 </Select.Item>
               ))}
             </Select.Category>
           )}
          <Select.Action
            onSelect={() => {
              navigate("/roles/new");
            }}
          >
            <Button
              kind="plain"
              endIcon="arrow-right"
              label={l10n.getString("org-role-create-new")}
            />
          </Select.Action>
        </Select>
      </div>

      <Row gapSize="m">
        <div className={styles.sidebar}>
          <ContentCard kind="bordered" paddingSize="none">
            <div className={styles.sidebarHeader}>
              <div className={styles.sidebarControls}>
                <div className={styles.searchInput}>
                  <TextInput
                    placeholder={l10n.getString("org-role-search-placeholder")}
                    startIcon={"search"}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setSearchQuery(event.target.value)
                    }
                    value={searchQuery}
                  />
                </div>
                <Button
                  startIcon="plus"
                  kind="primary"
                  size="m"
                  onClick={() => navigate("/roles/new")}
                >
                  {l10n.getString("org-role-button-create")}
                </Button>
              </div>
            </div>
            <div className={styles.rolesList}>
              {filteredDefaultRoles.length > 0 && (
                <div className={styles.roleSection}>
                  <p
                    className={`${styles.roleSectionHeader} fontSize--xs fontWeight--bold`}
                  >
                    {l10n.getString("org-role-section-default")}
                  </p>
                  <div className={styles.rolesContainer}>
                    {filteredDefaultRoles.map((role) => (
                      <RoleTile
                        key={role.uuid}
                        role={role}
                        selectedUuid={roleUuid}
                        organizationUsers={organizationUsers}
                        isDefault={true}
                        l10n={l10n}
                        onSelect={navigateToRole}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className={`${styles.roleSection} ${styles.customRoleSection}`}>
                <div
                  className={`${styles.roleSectionHeader} ${styles.roleSectionHeaderWithCount} fontSize--xs`}
                >
                  <span className="fontWeight--bold">{l10n.getString("org-role-section-custom")}</span>
                  <Chip
                    label={`${String(customRoles.length)}`}
                    kind="primary"
                  />
                </div>
                <div className={styles.rolesContainer}>
                  {filteredCustomRoles.length > 0 &&
                    filteredCustomRoles.map((role) => (
                      <RoleTile
                        key={role.uuid}
                        role={role}
                        selectedUuid={roleUuid}
                        organizationUsers={organizationUsers}
                        l10n={l10n}
                        onSelect={navigateToRole}
                      />
                    ))}
                  {customRoles.length === 0 && (
                    <div className={styles.emptyCustomRolesCard}>
                      <ContentCard kind="bordered">
                        <p className="fontColor--heading fontWeight--bold fontSize--m">
                          {l10n.getString("org-role-no-custom")}
                        </p>
                        <p className="fontSize--s">
                          {l10n.getString("org-role-tailor-access")}
                        </p>
                        <Button
                          endIcon="plus"
                          onClick={() => {
                            navigate("/roles/new");
                          }}
                        >
                          {l10n.getString("org-role-create-a-role")}
                        </Button>
                      </ContentCard>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ContentCard>
        </div>
        <div className={styles.content}>
          <RoleInfo
            role={selectedRole}
            organizationUsers={organizationUsers}
            allAccounts={allAccounts}
            orgLimits={orgLimits}
            hasInstitutionLoaded={hasInstitutionLoaded}
            l10n={l10n}
            assignableRoles={assignableRoles}
            onNavigateToEditRole={(selectedRoleUuid) => {
              navigate(`/roles/${selectedRoleUuid}/edit`);
            }}
            onNavigateToDuplicateRole={(selectedRoleUuid) => {
              navigate(`/roles/${selectedRoleUuid}/duplicate`);
            }}
          />
        </div>
      </Row>
    </div>
  );
}

export default function OrganizationRoles({
  organizationUuid,
  allAccounts,
}: OrganizationRolesProps) {
  const { l10n } = useLocalization();
  if (!organizationUuid) {
    return (
      <div style={{ minHeight: "800px", padding: "32px" }}>
        {l10n.getString("org-role-error-no-org-id")}
      </div>
    );
  }

  return (
    <OrganizationRolesContent
      allAccounts={allAccounts}
    />
  );
}
